// src/components/HopCanvasViewer.js
import React, { useCallback, useMemo, useRef, useEffect, Profiler, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Controls,
  useReactFlow,
  useViewport,
  Panel,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  Connection,
  OnConnect,
  OnNodesChange,
  OnEdgesChange,
  XYPosition,
  Viewport,
  MiniMapNodeProps,
  applyNodeChanges,
  applyEdgeChanges,
  getOutgoers,
  IsValidConnection,
} from '@xyflow/react';
import type { DragEvent, ClipboardEvent } from 'react';
import { Button, Tooltip, message, theme } from 'antd';
import {
  SaveOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  StopOutlined,
  BorderHorizontalOutlined,
  BorderVerticleOutlined,
  UndoOutlined,
  RedoOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { createContextLogger } from '../../../utils/logger';
import dagre from 'dagre';
import { shallow } from 'zustand/shallow';

const logger = createContextLogger('HopCanvasViewer');

import '@xyflow/react/dist/style.css';

// Import CustomEdge component
import CustomEdge from '../../edges/CustomEdge';
import edgeTypes from '../../edges/edgeTypes';

// Import optimized selectors from Store
import { usePipelineActions, useFlowActions, useStore, NodeData, EdgeData, useSelectedNodeId, useLastNodePosition, useUndoRedoState, Node as StoreNode, Edge as StoreEdge } from '../../../store/store';

// Import Node and Edge Types
import nodeTypes, { shouldShowPropertiesPanel } from '../../nodes/nodeTypes';

// Import icons
import TableInputIcon from '../../../assets/icons/tableinput.svg?react';
import TableOutputIcon from '../../../assets/icons/tableoutput.svg?react';
import FilterRowsIcon from '../../../assets/icons/filterrows.svg?react';

// Import usePerformanceMonitor
import { usePerformanceMonitor } from '../../../hooks/usePerformanceMonitor';

// Import createNodeAndMaybeEdge from utils
import { createNodeAndMaybeEdge, generateUniqueNodeName } from '../../../utils/nodeCreationUtils';

// Import centralized edge utilities
import { createOnConnectHandler } from '../../../utils/edgeUtils';

// Import delete utilities
import { deleteSelectedWithLogging } from '../../../utils/deleteUtils';

// Import tab manager store
import useTabManagerStore from '../../../store/tabManagerStore';

// Import telemetry service
import { telemetryService } from '../../../api/telemetry';

// Import dark mode context
import { useDarkMode } from '../../../contexts/DarkModeContext';

// Import RunPipelineDialog
import RunPipelineDialog from '../RunPipelineDialog';

// Import FileBrowserDialog
import FileBrowserDialog from '../../FileBrowserDialog';

// Import extracted hooks
import { useCanvasSave } from './hooks/useCanvasSave';


// Custom MiniMap node component with connection indicators
const CustomMiniMapNode: React.FC<MiniMapNodeProps> = ({ x, y, width, height, color, strokeColor, strokeWidth }) => {
  // Ensure minimum size for visibility
  const nodeWidth = Math.max(width || 12, 12);
  const nodeHeight = Math.max(height || 12, 12);
  
  return (
    <g>
      {/* Main node rectangle */}
      <rect
        x={x - nodeWidth / 2}
        y={y - nodeHeight / 2}
        width={nodeWidth}
        height={nodeHeight}
        fill={color}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        rx={2}
        ry={2}
      />
      {/* Connection indicators - small dots showing potential connection points */}
      <circle
        cx={x + nodeWidth / 2}
        cy={y}
        r={1.5}
        fill={strokeColor}
        opacity={0.4}
      />
      <circle
        cx={x - nodeWidth / 2}
        cy={y}
        r={1.5}
        fill={strokeColor}
        opacity={0.4}
      />
    </g>
  );
};

// Type definitions
interface HopCanvasViewerProps {
  pipelineId: string;
}

interface NodeInfo {
  type: string;
  nodeType: string;
  name: string;
  icon?: string;
}

interface HelperLine {
  x?: number;
  y?: number;
  orientation: 'horizontal' | 'vertical';
}

interface HelperLinesProps {
  lines: HelperLine[];
  viewport: Viewport;
}

interface NodeAlignmentPoints {
  x: number[];
  y: number[];
  width: number;
  height: number;
}

interface PipelineTransform {
  name: string;
  type: string;
}

interface PipelineHop {
  from: string;
  to: string;
  enabled?: boolean;
}

interface PipelineData {
  actionTransforms?: PipelineTransform[];
  hops?: PipelineHop[];
}

interface LayoutedElements {
  nodes: Node<NodeData>[];
  edges: Edge<EdgeData>[];
}

type IconComponent = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

type Direction = 'LR' | 'TB';

// Create a mapping from node type string to the icon component
const nodeTypeToIconMap: Record<string, IconComponent> = {
  TableInput: TableInputIcon,
  tableOutput: TableOutputIcon,
  filterRows: FilterRowsIcon,
};

// Memoize the nodeTypes object
const memoizedNodeTypes = nodeTypes;

const reactFlowContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const reactFlowWrapperStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  background: '#f8f8f8',
};

let idCounter = 0;
const getId = (): string => `hop_elem_${idCounter++}`;

// Helper function to find all downstream edges
const getDownstreamEdgesRecursive = (
  startEdgeId: string,
  allEdges: Edge<EdgeData>[],
  allNodes: Node<NodeData>[],
  visitedEdges = new Set<string>()
): string[] => {
  const downstreamEdgeIds = new Set<string>();
  const queue = [startEdgeId];

  while (queue.length > 0) {
    const currentEdgeId = queue.shift()!;

    if (visitedEdges.has(currentEdgeId)) {
      continue;
    }
    visitedEdges.add(currentEdgeId);

    const currentEdge = allEdges.find((e) => e.id === currentEdgeId);
    if (!currentEdge) {
      continue;
    }

    downstreamEdgeIds.add(currentEdgeId);

    const targetNodeId = currentEdge.target;
    const outgoingEdges = allEdges.filter((edge) => edge.source === targetNodeId);

    outgoingEdges.forEach((outEdge) => {
      if (!visitedEdges.has(outEdge.id)) {
        queue.push(outEdge.id);
      }
    });
  }
  return Array.from(downstreamEdgeIds);
};

// Add Profiler callback
const onRenderCallback = (
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number
): void => {
    //   logger.performance.debug('Component render:', {
    //   id,
    //   phase,
    //   actualDuration,
    //   baseDuration,
    //   startTime,
    //   commitTime,
    // });
};

// Add layout configuration
const getLayoutedElements = (
  nodes: Node<NodeData>[],
  edges: Edge<EdgeData>[],
  direction: Direction = 'LR'
): LayoutedElements => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure the graph
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 50,
    ranksep: direction === 'LR' ? 150 : 75, // Swapped the condition to match new direction values
    marginx: 50,
    marginy: 50,
  });

  // Add nodes to the graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 150, height: 40 });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Get the layouted nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 75, // Center the node
        y: nodeWithPosition.y - 20,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const SNAP_THRESHOLD = 12; // px (increased for smoother snapping)
const DEFAULT_NODE_WIDTH = 48; // Match NodeWrapper width in withNodeHandles.tsx
const DEFAULT_NODE_HEIGHT = 48; // Match NodeWrapper height in withNodeHandles.tsx

function getNodeAlignmentPoints(node: Node<NodeData>, isMovingNode = false): NodeAlignmentPoints {
  // The NodeWrapper is 48x48px as defined in withNodeHandles.tsx
  const NODE_WRAPPER_WIDTH = 48;
  const NODE_WRAPPER_HEIGHT = 48;
  
  // For helper lines to work correctly, we need to find the NodeWrapper position
  // within each node's measured area. The key insight is that we need to find
  // where the actual 48x48px square is positioned within the measured DOM element.
  
  let nodeWrapperOffsetX = 0;
  let nodeWrapperOffsetY = 0;
  
  if (typeof document !== 'undefined') {
    const nodeElement = document.querySelector(`[data-id="${node.id}"]`);
    if (nodeElement) {
      // Find the NodeWrapper element within the node
      const nodeWrapperElement = nodeElement.querySelector('[data-testid="react-flow-node"]');
      if (nodeWrapperElement && nodeElement) {
        const nodeRect = nodeElement.getBoundingClientRect();
        const wrapperRect = nodeWrapperElement.getBoundingClientRect();
        
        // Calculate the offset of the NodeWrapper within the full node element
        nodeWrapperOffsetX = wrapperRect.left - nodeRect.left;
        nodeWrapperOffsetY = wrapperRect.top - nodeRect.top;
        
        // if (isMovingNode) {
        //   logger.debug('NodeWrapper position within node:', {
        //     nodeId: node.id,
        //     position: node.position,
        //     nodeRect: { width: nodeRect.width, height: nodeRect.height },
        //     wrapperRect: { width: wrapperRect.width, height: wrapperRect.height },
        //     offsets: { x: nodeWrapperOffsetX, y: nodeWrapperOffsetY },
        //     alignmentPoints: {
        //       left: node.position.x + nodeWrapperOffsetX,
        //       right: node.position.x + nodeWrapperOffsetX + NODE_WRAPPER_WIDTH,
        //       top: node.position.y + nodeWrapperOffsetY,
        //       bottom: node.position.y + nodeWrapperOffsetY + NODE_WRAPPER_HEIGHT
        //     }
        //   });
        // }
      }
    }
  }
  
  return {
    x: [node.position.x + nodeWrapperOffsetX, node.position.x + nodeWrapperOffsetX + NODE_WRAPPER_WIDTH], // left and right edges of NodeWrapper
    y: [node.position.y + nodeWrapperOffsetY, node.position.y + nodeWrapperOffsetY + NODE_WRAPPER_HEIGHT], // top and bottom edges of NodeWrapper
    width: NODE_WRAPPER_WIDTH,
    height: NODE_WRAPPER_HEIGHT,
  };
}

function getAlignmentLines(nodes: Node<NodeData>[], movingNodeId: string): HelperLine[] {
  const lines: HelperLine[] = [];
  const movingNode = nodes.find((n) => n.id === movingNodeId);
  if (!movingNode) return lines;
  const movingPoints = getNodeAlignmentPoints(movingNode, true);

  nodes.forEach((node) => {
    if (node.id === movingNodeId) return;
    const points = getNodeAlignmentPoints(node, false);

    // Debug: Log the dimensions being used for alignment
    logger.debug(`Node ${node.id} alignment points:`, {
      position: node.position,
      measured: (node as any).measured,
      width: node.width,
      height: node.height,
      alignmentPoints: points,
      rightEdge: node.position.x + points.width,
      bottomEdge: node.position.y + points.height
    });

    // Check all x alignments (left of moving node to left/right of other nodes)
    movingPoints.x.forEach((mx, mi) => {
      points.x.forEach((px, pi) => {
        if (Math.abs(mx - px) < SNAP_THRESHOLD) {
          lines.push({
            x: px,
            orientation: 'vertical',
          });
        }
      });
    });

    // Check all y alignments (top of moving node to top/bottom of other nodes)
    movingPoints.y.forEach((my, mi) => {
      points.y.forEach((py, pi) => {
        if (Math.abs(my - py) < SNAP_THRESHOLD) {
          lines.push({
            y: py,
            orientation: 'horizontal',
          });
        }
      });
    });
  });

  return lines;
}


const HelperLines: React.FC<HelperLinesProps> = ({ lines, viewport }) => {
  if (!lines || lines.length === 0) return null;

  return (
    <>
      {lines.map((line, index) => {
        if (line.orientation === 'vertical') {
          // Transform flow coordinates to screen coordinates
          const screenX = line.x! * viewport.zoom + viewport.x;
  return (
    <div
              key={index}
      style={{
        position: 'absolute',
                left: screenX - 1, // Center the 2px line
                top: -10000, // Extend far beyond visible area
                width: 2,
                height: 20000, // Make it very tall
                background: 'linear-gradient(to right, #d1d5db, #9ca3af, #d1d5db)',
                boxShadow: '0 0 2px rgba(0,0,0,0.1)',
        zIndex: 1000,
                pointerEvents: 'none',
              }}
            />
          );
        } else {
          // Transform flow coordinates to screen coordinates
          const screenY = line.y! * viewport.zoom + viewport.y;
          return (
        <div
          key={index}
          style={{
            position: 'absolute',
                top: screenY - 1, // Center the 2px line
                left: -10000, // Extend far beyond visible area
                width: 20000, // Make it very wide
                height: 2,
                background: 'linear-gradient(to bottom, #d1d5db, #9ca3af, #d1d5db)',
                boxShadow: '0 0 2px rgba(0,0,0,0.1)',
                zIndex: 1000,
                pointerEvents: 'none',
          }}
        />
          );
        }
      })}
    </>
  );
};

// Wrap the component with ReactFlowProvider
const HopCanvasViewer: React.FC<HopCanvasViewerProps> = ({ pipelineId }) => {
  const { token } = theme.useToken();
  const { setActivePipeline } = usePipelineActions();
  usePerformanceMonitor('HopCanvasViewer', { pipelineId });

  // Initialize pipeline if it doesn't exist
  useEffect(() => {
    if (pipelineId) {
      setActivePipeline(pipelineId);
    }
  }, [pipelineId, setActivePipeline]);

  return (
    <Profiler id="HopCanvasViewer" onRender={onRenderCallback}>
      <div style={reactFlowContainerStyle}>
        <ReactFlowProvider>
          <FlowCanvas pipelineId={pipelineId} />
        </ReactFlowProvider>
      </div>
    </Profiler>
  );
};

HopCanvasViewer.displayName = 'HopCanvasViewer';

// Main FlowCanvas component
const FlowCanvas: React.FC<HopCanvasViewerProps> = React.memo(({ pipelineId }) => {
  const { token } = theme.useToken();
  const { isDarkMode } = useDarkMode();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { getViewport, setViewport, getZoom, getNodes: getReactFlowNodes, getInternalNode } = useReactFlow();
  const viewport = useViewport();
  const storeApi = useStore.getState();

  // Performance monitoring
  usePerformanceMonitor('HopCanvasViewer', { pipelineId });

  // Local state to track selected nodes (synced with ReactFlow)
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    flowPosition?: XYPosition;
  }>({
    show: false,
    x: 0,
    y: 0,
  });

  // Use optimized selectors
  const { nodes, edges, getNodes, getEdges, hasStructuralChanges } = useStore(
    (state) => ({
      nodes: (state.pipelines.find((p) => p.id === pipelineId)?.nodes || []),
      edges: state.pipelines.find((p) => p.id === pipelineId)?.edges || [],
      getNodes: () => state.pipelines.find((p) => p.id === pipelineId)?.nodes || [],
      getEdges: () => state.pipelines.find((p) => p.id === pipelineId)?.edges || [],
      hasStructuralChanges: state.pipelines.find((p) => p.id === pipelineId)?.hasStructuralChanges || false,
    }),
    shallow
  );

  // Use store actions and selectors
  const { 
    addNode, 
    updateNode, 
    deleteNode, 
    addEdge, 
    updateEdge, 
    deleteEdge, 
    setSelectedNodeId,
    setLastNodePosition,
    saveToHistory,
    undo,
    redo,
    canUndo,
    canRedo
  } = useFlowActions();
  const { setActivePipeline, migratePipelineId, markStructuralChange, clearStructuralChanges, updatePipeline } = usePipelineActions();
  const { nodeId: selectedNodeId } = useSelectedNodeId();
  const lastNodePosition = useLastNodePosition();
  const undoRedoState = useUndoRedoState();
  
  // Get transform state management methods from store
  const { updateTransformState, getTransformState } = useStore((state) => ({
    updateTransformState: state.updateTransformState,
    getTransformState: state.getTransformState,
  }));
  
  // Get tab manager store methods
  const { updateTabModified, updateTabPath, updateTabName, updateTabIsNew, migrateTabId, tabs } = useTabManagerStore((state) => ({
    updateTabModified: state.updateTabModified,
    updateTabPath: state.updateTabPath,
    updateTabName: state.updateTabName,
    updateTabIsNew: state.updateTabIsNew,
    migrateTabId: state.migrateTabId,
    tabs: state.tabs,
  }));
  
  // Get current tab's modified state and type
  const currentTab = tabs.find(tab => tab.id === pipelineId);
  const isTabModified = currentTab?.isModified ?? false;
  const tabType = currentTab?.type || 'pipeline'; // 'pipeline' or 'workflow'

  // Set this pipeline as active when it's being viewed
  // Track if this is the first render for this pipeline
  const initializedPipelines = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    setActivePipeline(pipelineId);
    
    // Save initial state to history ONLY when pipeline is first opened (not on every node change)
    if (!initializedPipelines.current.has(pipelineId)) {
      initializedPipelines.current.add(pipelineId);
      if (nodes.length > 0 || edges.length > 0) {
        saveToHistory('Initial state');
      }
    }
  }, [pipelineId, setActivePipeline, nodes.length, edges.length, saveToHistory]);

  // Note: handleSave, handleSaveFile, handleSaveAs are now provided by useCanvasSave hook below
  
  /* OLD SAVE HANDLER - REPLACED BY useCanvasSave HOOK
  const handleSave = useCallback(async () => {
    try {
      // Import the transforms service
      const { transformsService } = await import('../../api/services/transforms');
      
      // Find all modified transforms in the current pipeline
      const modifiedTransforms = nodes.filter((node) => {
        const transformState = getTransformState(node.id);
        return transformState?.isModified === true;
      });

      logger.info('Saving pipeline with modified transforms:', {
        pipelineId,
        modifiedCount: modifiedTransforms.length,
        totalNodes: nodes.length
      });

      // Send API calls for each modified transform
      const savePromises = modifiedTransforms.map(async (node) => {
        try {
          const currentName = node.data?.name || node.id;
          const originalName = node.data?._state?.originalValues?.name || currentName;
          
          // Extract only the relevant transform values, excluding internal/UI fields
          const internalFields = ['_state', '_cachedValues', 'iconType', 'nodeType', 'label', 'svg', 'incomingFields', 'outgoingFields'];
          const transformValues: Record<string, any> = {};
          
          // Copy all fields from node.data except internal ones
          if (node.data) {
            Object.keys(node.data).forEach((key) => {
              if (!internalFields.includes(key)) {
                transformValues[key] = node.data[key];
              }
            });
          }
          
          // Create ActionTransformValues structure matching the backend DTO
          const actionTransformValues = {
            id: node.id,
            type: node.data?.type || 'Unknown',
            xLocation: Math.round(node.position.x),
            yLocation: Math.round(node.position.y),
            values: transformValues
          };

          // Always use original name for endpoint lookup (transformName in path)
          // This becomes transformId in the backend to find the transform in PipelineMeta
          // If originalName doesn't exist (new transform), use currentName
          const transformName = originalName || currentName;
          
          logger.debug('Saving transform:', {
            currentName,
            originalName,
            transformName, // The name used in the endpoint
            isRenamed: currentName !== originalName,
            nodeId: node.id,
            transformType: node.data?.type,
            xLocation: Math.round(node.position.x),
            yLocation: Math.round(node.position.y),
            valuesKeys: Object.keys(transformValues)
          });

          const result = await transformsService.postTransform(
            transformName, // This becomes transformId in backend to find the transform
            pipelineId, // Using pipelineId as the path parameter
            actionTransformValues
          );

          // Log the full response value for debugging
          logger.debug('Transform save response:', {
            transformName, // The name used in the endpoint
            currentName,
            originalName,
            nodeId: node.id,
            fullResponse: JSON.stringify(result, null, 2),
            isValid: result.isValid,
            valid: result.valid,
            errors: result.errors
          });

          // Handle both 'valid' and 'isValid' for backward compatibility
          // Backend returns 'isValid', frontend may expect 'valid'
          const isValid = result.isValid !== undefined 
            ? result.isValid 
            : (result.valid ?? true);
          
          if (!isValid) {
            logger.warn('Transform validation failed:', {
              transformName,
              nodeId: node.id,
              errors: result.errors,
              fullResult: result
            });
          }

          return { nodeId: node.id, success: isValid, errors: result.errors };
        } catch (error) {
          logger.error('Failed to save transform:', {
            nodeId: node.id,
            transformName: node.data?.name,
            error
          });
          return { nodeId: node.id, success: false, error: error instanceof Error ? error.message : String(error) };
        }
      });

      // Wait for all save operations to complete
      const results = await Promise.all(savePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      // Only update transform state for successfully saved nodes
      // Failed nodes should remain marked as modified so user can fix and retry
      const savedNodeIds = new Set(results.filter(r => r.success).map(r => r.nodeId));
      const now = new Date().toISOString();
      
      nodes.forEach((node) => {
        if (savedNodeIds.has(node.id)) {
          // Successfully saved - mark as saved and not modified
          updateTransformState(node.id, {
            cacheValid: false,
            isModified: false,
            isNew: false,
            lastSaved: now,
            source: 'user'
          });
        } else {
          // Save failed - keep it as modified so user can fix and retry
          logger.debug('Keeping transform as modified due to save failure:', {
            nodeId: node.id,
            errors: results.find(r => r.nodeId === node.id)?.errors
          });
        }
      });
      
      // Only mark tab as saved if ALL saves succeeded
      if (failureCount === 0) {
        updateTabModified(pipelineId, false);
        message.success(`Pipeline saved successfully (${successCount} transforms saved)`);
      } else {
        // Keep tab marked as modified if any saves failed
        const failedResults = results.filter(r => !r.success);
        const failedNodes = failedResults.map(r => {
          const node = nodes.find(n => n.id === r.nodeId);
          return node?.data?.name || r.nodeId;
        });
        
        // Format error messages from ValidationResultDTO
        const formatErrors = (errors?: Record<string, string[]>): string => {
          if (!errors || Object.keys(errors).length === 0) {
            return 'Validation failed';
          }
          
          const errorMessages: string[] = [];
          Object.entries(errors).forEach(([field, messages]) => {
            if (messages && messages.length > 0) {
              const fieldLabel = field === 'general' ? 'General' : field;
              if (messages.length === 1) {
                errorMessages.push(`${fieldLabel}: ${messages[0]}`);
              } else {
                errorMessages.push(`${fieldLabel}: ${messages.join('; ')}`);
              }
            }
          });
          
          return errorMessages.join('. ') || 'Validation failed';
        };
        
        // Show detailed error messages for each failed transform
        failedResults.forEach((failedResult) => {
          const node = nodes.find(n => n.id === failedResult.nodeId);
          const transformName = node?.data?.name || failedResult.nodeId;
          const errorMessage = formatErrors(failedResult.errors);
          
          // Show error notification with transform name and specific error messages
          message.error(`${transformName}: ${errorMessage}`, 8);
        });
        
        // Also show a summary message if multiple transforms failed
        if (failureCount > 1) {
          message.warning(
            `Failed to save ${failureCount} transform(s). See error messages above for details.`,
            6
          );
        }
        
        logger.error('Save failed for transforms:', {
          failedNodes,
          errors: failedResults.map(r => ({
            nodeId: r.nodeId,
            errors: r.errors
          }))
        });
      }

      logger.info('Pipeline save completed:', { 
        pipelineId, 
        totalNodes: nodes.length,
        modifiedTransforms: modifiedTransforms.length,
        successCount,
        failureCount
      });
    } catch (error) {
      message.error('Failed to save pipeline');
      logger.error('Failed to save pipeline:', error);
    }
  }, [nodes, updateTransformState, updateTabModified, pipelineId, getTransformState]);
  */

  // Define the onChange handlers using React Flow's CORRECT pattern
  const onNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // ✅ STEP 1: Apply changes using React Flow's applyNodeChanges
      // This is THE CORRECT WAY according to React Flow documentation
      const updatedNodes = applyNodeChanges(changes, nodes);
      
      // ✅ STEP 2: Update the entire nodes array in Zustand
      updatePipeline(pipelineId, { nodes: updatedNodes });
      
      // ✅ STEP 3: Handle app-specific logic (history, tracking, etc.)
      let hasModifications = false;
      let hasStructuralChange = false;
      
      // Check if we need to save to history BEFORE making changes
      const needsHistorySave = changes.some(change => {
        if (change.type === 'remove') return true;
        if (change.type === 'position' && 'dragging' in change && change.dragging === false) return true;
        if (change.type === 'position' && !('dragging' in change)) return true;
        return false;
      });
      
      if (needsHistorySave) {
        const changeDescriptions = changes
          .filter(change => {
            if (change.type === 'remove') return true;
            if (change.type === 'position') {
              if ('dragging' in change) {
                return change.dragging === false;
              }
              return true;
            }
            return false;
          })
          .map(change => {
            if (change.type === 'remove') return 'Remove node';
            if (change.type === 'position') return 'Move node';
            return '';
          })
          .filter(Boolean)
          .join(', ');
        
        saveToHistory(changeDescriptions);
      }
      
      // Track changes for app logic
      changes.forEach((change) => {
        switch (change.type) {
          case 'position':
            if (change.position) {
              hasModifications = true;
              
              // Update lastNodePosition when drag ends
              if ('dragging' in change && change.dragging === false) {
                hasStructuralChange = true;
                setLastNodePosition(change.position);
              } else if (!('dragging' in change)) {
                hasStructuralChange = true;
              }
            }
            break;
          case 'remove':
            hasModifications = true;
            hasStructuralChange = true;
            break;
          case 'add':
            hasModifications = true;
            hasStructuralChange = true;
            break;
          case 'select':
          case 'dimensions':
          case 'reset':
            // These are handled by applyNodeChanges
            break;
          default:
            break;
        }
      });
      
      if (hasModifications) {
        updateTabModified(pipelineId, true);
      }
      
      if (hasStructuralChange) {
        markStructuralChange(pipelineId);
      }
    },
    [nodes, pipelineId, updatePipeline, updateTabModified, saveToHistory, setLastNodePosition, markStructuralChange]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // ✅ Apply changes using React Flow's applyEdgeChanges for React Flow-initiated changes
      const updatedEdges = applyEdgeChanges(changes, edges);
      
      // ✅ Update the entire edges array in Zustand
      updatePipeline(pipelineId, { edges: updatedEdges });
      
      // Handle app-specific logic
      let hasModifications = false;
      let hasStructuralChange = false;
      
      // Check if we need to save to history
      const needsHistorySave = changes.some(change => change.type === 'remove');
      
      if (needsHistorySave) {
        const changeDescriptions = changes
          .filter(change => change.type === 'remove')
          .map(change => 'Remove edge')
          .join(', ');
        
        saveToHistory(changeDescriptions);
      }
      
      // Track changes for app logic
      changes.forEach((change) => {
        switch (change.type) {
          case 'remove':
            hasModifications = true;
            hasStructuralChange = true;
            break;
          case 'add':
            hasModifications = true;
            hasStructuralChange = true;
            break;
          case 'select':
          case 'reset':
            // These are handled by applyEdgeChanges
            break;
          default:
            break;
        }
      });
      
      if (hasModifications) {
        updateTabModified(pipelineId, true);
      }
      
      if (hasStructuralChange) {
        markStructuralChange(pipelineId);
      }
    },
    [edges, pipelineId, updatePipeline, updateTabModified, saveToHistory, markStructuralChange]
  );

  // Handle selection changes to keep selectedNodes state in sync with ReactFlow
  const onSelectionChange = useCallback(
    ({ nodes: reactFlowSelectedNodes, edges: selectedEdges }: { nodes: Node[]; edges: Edge[] }) => {
      setSelectedNodes(reactFlowSelectedNodes);
      
      // For single node selection, update the selectedNodeId for properties panel and edge creation
      if (reactFlowSelectedNodes.length === 1) {
        const selectedNodeId = reactFlowSelectedNodes[0].id;
        const currentPipeline = useStore.getState().pipelines.find(p => p.id === pipelineId);
        const currentSelectedNodeId = currentPipeline?.selectedNodeId;
        const currentIsDoubleClicked = currentPipeline?.isNodeDoubleClicked || false;
        
        // If this is the same node that's already selected and double-clicked,
        // don't interfere with the selection state at all to prevent closing the properties panel
        const isSameNodeAlreadyDoubleClicked = selectedNodeId === currentSelectedNodeId && currentIsDoubleClicked;
        
        if (isSameNodeAlreadyDoubleClicked) {
          logger.flow.debug('🔄 onSelectionChange: preserving double-clicked state for same node:', {
            nodeId: selectedNodeId,
            currentIsDoubleClicked,
            action: 'no-op to preserve properties panel'
          });
          // Don't call setSelectedNodeId at all to avoid any state changes
          return;
        }
        
        // For new node selections or when not double-clicked, proceed with normal logic
        const isSameNode = selectedNodeId === currentSelectedNodeId;
        const preserveDoubleClick = isSameNode && currentIsDoubleClicked;
        
        logger.flow.debug('🔄 onSelectionChange:', {
          nodeId: selectedNodeId,
          isSameNode,
          currentIsDoubleClicked,
          preserveDoubleClick,
          currentSelectedNodeId
        });
        
        setSelectedNodeId(selectedNodeId, preserveDoubleClick);
      } else {
        // For multi-selection or no selection, clear the selectedNodeId
        // But only if we're not currently in a double-clicked state (to avoid closing properties panel)
        const currentPipeline = useStore.getState().pipelines.find(p => p.id === pipelineId);
        const currentIsDoubleClicked = currentPipeline?.isNodeDoubleClicked || false;
        
        if (currentIsDoubleClicked) {
          logger.flow.debug('🔄 onSelectionChange: skipping clear because properties panel is open');
          return;
        }
        
        logger.flow.debug('🔄 onSelectionChange: clearing selection');
        setSelectedNodeId(null, false);
      }
    },
    [setSelectedNodeId, pipelineId]
  );

  // Validate connections to prevent cycles and reverse edges
  const isValidConnection: IsValidConnection = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) {
        return false;
      }

      // With Easy Connect, React Flow swaps source/target when dragging
      // We need to swap them back for validation (same as in onConnect)
      const actualSource = connection.target;
      const actualTarget = connection.source;

      // Prevent self-connections
      if (actualSource === actualTarget) {
        logger.debug('❌ Invalid connection: self-connection', {
          source: actualSource,
          target: actualTarget,
        });
        return false;
      }

      // Get current nodes and edges from React Flow
      const currentNodes = getReactFlowNodes();
      const currentEdges = edges;

      // Find the target node
      const targetNode = currentNodes.find((node) => node.id === actualTarget);
      if (!targetNode) {
        logger.debug('❌ Invalid connection: target node not found', {
          target: actualTarget,
        });
        return false;
      }

      // Check for reverse connection (if A -> B exists, prevent B -> A)
      const reverseEdgeExists = currentEdges.some(
        (edge) => edge.source === actualTarget && edge.target === actualSource
      );
      if (reverseEdgeExists) {
        logger.debug('❌ Invalid connection: reverse edge already exists', {
          source: actualSource,
          target: actualTarget,
        });
        return false;
      }

      // Check for cycles using getOutgoers
      // If connecting actualTarget -> actualSource would create a cycle, prevent it
      const hasCycle = (node: Node, visited = new Set<string>()): boolean => {
        // If we've already visited this node, we found a cycle
        if (visited.has(node.id)) {
          return false; // Not a cycle, just revisiting
        }

        visited.add(node.id);

        // Check if this node has any outgoers that would lead back to the source
        const outgoers = getOutgoers(node, currentNodes, currentEdges);
        for (const outgoer of outgoers) {
          // If any outgoer is the source node, we have a cycle
          if (outgoer.id === actualSource) {
            return true;
          }
          // Recursively check if this outgoer leads to a cycle
          if (hasCycle(outgoer, visited)) {
            return true;
          }
        }

        return false;
      };

      // Check if connecting actualTarget -> actualSource would create a cycle
      if (hasCycle(targetNode)) {
        logger.debug('❌ Invalid connection: would create a cycle', {
          source: actualSource,
          target: actualTarget,
        });
        return false;
      }

      logger.debug('✅ Valid connection', {
        source: actualSource,
        target: actualTarget,
      });
      return true;
    },
    [getReactFlowNodes, edges]
  );

  // Handle connection between nodes
  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        logger.debug('🔗 onConnect called:', {
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
        });
        
        // With Easy Connect, React Flow swaps source/target when dragging
        // The node you drag FROM becomes the target, and where you DROP becomes the source
        // We need to swap them back for correct data flow direction
        const actualSource = connection.target;
        const actualTarget = connection.source;
        
        logger.debug('🔄 Corrected direction:', {
          actualSource,
          actualTarget,
        });
        
        // Save to history BEFORE making the change
        saveToHistory('Add connection');
        
        // Generate unique edge ID
        const edgeId = `${actualSource}-${actualTarget}`;
        
        const newEdge: StoreEdge = {
          id: edgeId,
          source: actualSource,
          target: actualTarget,
          type: 'custom',
          data: {
            enabled: true,
            condition: 'unconditional',
            fields: []
          },
        };
        addEdge(newEdge); // Add the complete edge with ID
        
        // Mark tab as modified when new edge is created
        updateTabModified(pipelineId, true);
        
        // Mark structural change for edge addition
        markStructuralChange(pipelineId);
        
        logger.info('✅ Edge created:', {
          from: actualSource,
          to: actualTarget,
        });
      }
    },
    [addEdge, updateTabModified, pipelineId, saveToHistory, markStructuralChange]
  );

  // Helper lines state
  const [helperLines, setHelperLines] = React.useState<HelperLine[]>([]);
  const [draggingNodeId, setDraggingNodeId] = React.useState<string | null>(null);
  const [dragStartPosition, setDragStartPosition] = React.useState<XYPosition | null>(null);
  const [currentSnapPosition, setCurrentSnapPosition] = React.useState<{ x: number | null; y: number | null }>({ x: null, y: null });
  
  // Throttle helper lines updates to improve performance
  const lastHelperLineUpdate = useRef<number>(0);
  const HELPER_LINE_THROTTLE_MS = 32; // ~60fps, adjust higher (e.g., 32ms for ~30fps) if still choppy

  // Proximity connect - minimum distance for auto-connection (in pixels)
  const MIN_PROXIMITY_DISTANCE = 250;

  // Get the closest node to the given node
  const getClosestNode = useCallback((node: Node) => {
    const internalNode = getInternalNode(node.id);
    if (!internalNode) return null;

    const allNodes = getNodes();
    
    const closestNode = allNodes.reduce(
      (result, n) => {
        if (n.id !== internalNode.id) {
          const nInternal = getInternalNode(n.id);
          if (!nInternal) return result;

          const dx = 
            (nInternal.internals?.positionAbsolute?.x ?? nInternal.position.x) - 
            (internalNode.internals?.positionAbsolute?.x ?? internalNode.position.x);
          const dy = 
            (nInternal.internals?.positionAbsolute?.y ?? nInternal.position.y) - 
            (internalNode.internals?.positionAbsolute?.y ?? internalNode.position.y);
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < result.distance && distance < MIN_PROXIMITY_DISTANCE) {
            result.distance = distance;
            result.node = nInternal;
          }
        }

        return result;
      },
      {
        distance: Number.MAX_VALUE,
        node: null as Node | null,
      }
    );

    if (!closestNode.node) {
      return null;
    }

    // Determine source/target based on relative position
    const closeNodeIsSource =
      (closestNode.node.internals?.positionAbsolute?.x ?? closestNode.node.position.x) <
      (internalNode.internals?.positionAbsolute?.x ?? internalNode.position.x);

    return {
      id: closeNodeIsSource
        ? `${closestNode.node.id}-${node.id}`
        : `${node.id}-${closestNode.node.id}`,
      source: closeNodeIsSource ? closestNode.node.id : node.id,
      target: closeNodeIsSource ? node.id : closestNode.node.id,
    };
  }, [getInternalNode, getNodes, MIN_PROXIMITY_DISTANCE]);

  // Handle node drag for proximity connect
  const onNodeDrag = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const closeEdge = getClosestNode(node);

      if (closeEdge) {
        // Check if this edge doesn't already exist
        const edgeExists = edges.some(
          (e) => e.source === closeEdge.source && e.target === closeEdge.target
        );

        if (!edgeExists) {
          // Add temporary edge to show proximity connection
          const tempEdge: StoreEdge = {
            ...closeEdge,
            type: 'custom',
            className: 'temp-proximity-edge',
            style: { strokeDasharray: '5, 5', opacity: 0.5 },
            data: {
              enabled: true,
              condition: 'unconditional',
              fields: [],
            },
          };

          // Remove old temp edges and add new one
          updatePipeline(pipelineId, {
            edges: [...edges.filter((e) => e.className !== 'temp-proximity-edge'), tempEdge],
          });
        }
      } else {
        // Remove temp edges when not in proximity
        updatePipeline(pipelineId, {
          edges: edges.filter((e) => e.className !== 'temp-proximity-edge'),
        });
      }
    },
    [getClosestNode, edges, pipelineId, updatePipeline]
  );

  // Handle node drag stop for proximity connect
  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const closeEdge = getClosestNode(node);

      // First, always remove temp edges
      const filteredEdges = edges.filter((e) => e.className !== 'temp-proximity-edge');
      updatePipeline(pipelineId, { edges: filteredEdges });

      if (closeEdge) {
        // Check if this edge doesn't already exist (check in filtered edges, not original edges)
        const edgeExists = filteredEdges.some(
          (e) => e.source === closeEdge.source && e.target === closeEdge.target
        );

        if (!edgeExists) {
          // Validate the connection before creating it (prevent cycles and reverse edges)
          const connection: Connection = {
            source: closeEdge.target, // Note: Easy Connect swaps these, so we need to match onConnect
            target: closeEdge.source,
          };
          
          if (!isValidConnection(connection)) {
            logger.debug('🔗 Proximity connect: Invalid connection, skipping', {
              source: closeEdge.source,
              target: closeEdge.target,
              reason: 'would create cycle or reverse edge',
            });
            return;
          }

          logger.info('🔗 Proximity connect: Creating edge', {
            source: closeEdge.source,
            target: closeEdge.target,
            method: 'using same code as onConnect',
          });

          // Use the EXACT same code as onConnect handler - copy the logic directly
          saveToHistory('Add connection');
        
          // Generate unique edge ID
          const edgeId = `${closeEdge.source}-${closeEdge.target}`;
          
          const newEdge: StoreEdge = {
            id: edgeId,
            source: closeEdge.source,
            target: closeEdge.target,
            type: 'custom',
            data: {
              enabled: true,
              condition: 'unconditional',
              fields: []
            },
          };
          
          logger.info('🔗 Proximity connect: Adding edge to store', { edgeId, newEdge });
          addEdge(newEdge); // Add the complete edge with ID
          
          // Mark tab as modified when new edge is created
          updateTabModified(pipelineId, true);
          
          // Mark structural change for edge addition
          markStructuralChange(pipelineId);
          
          logger.info('🔗 Proximity connection created successfully');
        } else {
          logger.debug('🔗 Proximity connect: Edge already exists, skipping', {
            source: closeEdge.source,
            target: closeEdge.target,
            existingEdges: filteredEdges.map(e => `${e.source}->${e.target}`),
          });
        }
      }
    },
    [
      getClosestNode,
      edges,
      pipelineId,
      updatePipeline,
      addEdge,
      saveToHistory,
      updateTabModified,
      markStructuralChange,
      isValidConnection,
    ]
  );

  const handleNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Check for modifications that should mark tab as modified
      let hasModifications = false;
      changes.forEach((change) => {
        if (change.type === 'remove' || change.type === 'add') {
          hasModifications = true;
        }
      });
      
      // Mark tab as modified if there were structural changes
      if (hasModifications) {
        updateTabModified(pipelineId, true);
      }
      
      // Helper lines and snapping logic
      changes.forEach((change) => {
        if (change.type === 'position' && 'dragging' in change && change.dragging) {
          setDraggingNodeId(change.id);

          // Store the initial drag position if not already set
          if (!dragStartPosition && change.position) {
            setDragStartPosition(change.position);
          }

          // Update helper lines based on current position (throttled for performance)
          if (change.position) {
            const now = Date.now();
            const shouldUpdate = now - lastHelperLineUpdate.current >= HELPER_LINE_THROTTLE_MS;
            
            // Get nodes once for both operations

            
            // Only update helper lines when throttle allows (expensive operation)
            if (shouldUpdate) {
                const reactFlowNodes = getReactFlowNodes();
              lastHelperLineUpdate.current = now;
              const updatedNodes = reactFlowNodes.map((n) =>
                n.id === change.id ? { ...n, position: change.position! } : n
              );
              setHelperLines(getAlignmentLines(updatedNodes as Node<NodeData>[], change.id));


            // Snapping logic runs every frame (less expensive, needs to be responsive)
            const movingNode = reactFlowNodes.find((n) => n.id === change.id);
              if (movingNode) {
              let bestSnapX: number | null = null;
              let bestSnapY: number | null = null;
              let snapDistanceX = Infinity;
              let snapDistanceY = Infinity;

              // Find the closest snap points - use reactFlowNodes to match getAlignmentLines
              const movingPoints = getNodeAlignmentPoints({ ...movingNode, position: change.position! } as Node<NodeData>, true);
              
              // logger.debug('Moving node edges:', {
              //   left: movingPoints.x[0],
              //   right: movingPoints.x[1],
              //   top: movingPoints.y[0],
              //   bottom: movingPoints.y[1]
              // });
              
              reactFlowNodes.forEach((node) => {
                if (node.id === change.id) return;
                const points = getNodeAlignmentPoints(node as Node<NodeData>, false);

                // Check X alignment (vertical lines) - with priority system
                movingPoints.x.forEach((mx, movingIndex) => {
                  points.x.forEach((ox) => {
                    const distance = Math.abs(mx - ox);
                    if (distance < SNAP_THRESHOLD && distance < snapDistanceX) {
                      // Priority: left edge (index 0) over right edge (index 1)
                      if (snapDistanceX === Infinity || movingIndex === 0 || 
                          (movingIndex === 1 && snapDistanceX > distance)) {
                        
                        // Calculate the node position that would align the NodeWrapper edges
                        // movingPoints.x[0] is the left edge of the NodeWrapper
                        // movingPoints.x[1] is the right edge of the NodeWrapper
                        // We need to calculate what the node.position.x should be
                        const nodeWrapperOffsetX = movingPoints.x[0] - change.position!.x;
                        
                        if (movingIndex === 0) {
                          // Aligning left edges: target_left = moving_left
                          bestSnapX = ox - nodeWrapperOffsetX;
                        } else {
                          // Aligning right edges: target_right = moving_right
                          bestSnapX = ox - nodeWrapperOffsetX - movingPoints.width;
                        }
                        
                        snapDistanceX = distance;
                        logger.debug(`X-snap: ${movingIndex === 0 ? 'LEFT' : 'RIGHT'} edge to ${ox}, node position: ${bestSnapX}`);
                      }
                    }
                  });
                });

                // Check Y alignment (horizontal lines) - with priority system  
                movingPoints.y.forEach((my, movingIndex) => {
                  points.y.forEach((oy) => {
                    const distance = Math.abs(my - oy);
                    if (distance < SNAP_THRESHOLD && distance < snapDistanceY) {
                      // Priority: top edge (index 0) over bottom edge (index 1)
                      if (snapDistanceY === Infinity || movingIndex === 0 || 
                          (movingIndex === 1 && snapDistanceY > distance)) {
                        
                        // Calculate the node position that would align the NodeWrapper edges
                        // movingPoints.y[0] is the top edge of the NodeWrapper
                        // movingPoints.y[1] is the bottom edge of the NodeWrapper
                        const nodeWrapperOffsetY = movingPoints.y[0] - change.position!.y;
                        
                        if (movingIndex === 0) {
                          // Aligning top edges: target_top = moving_top
                          bestSnapY = oy - nodeWrapperOffsetY;
                        } else {
                          // Aligning bottom edges: target_bottom = moving_bottom
                          bestSnapY = oy - nodeWrapperOffsetY - movingPoints.height;
                        }
                        
                        snapDistanceY = distance;
                        logger.debug(`Y-snap: ${movingIndex === 0 ? 'TOP' : 'BOTTOM'} edge to ${oy}, node position: ${bestSnapY}`);
                      }
                    }
                  });
                });
              });

              // Determine final position with sticky snapping
              const finalPosition = { ...change.position };
              let newSnapPosition = { ...currentSnapPosition };

              // Initialize snap position if it doesn't exist
              if (!newSnapPosition || typeof newSnapPosition !== 'object') {
                newSnapPosition = { x: null, y: null };
              }

              const breakoutThreshold = SNAP_THRESHOLD * 1.5;

              // Handle X-axis snapping
              if (newSnapPosition.x !== null) {
                // Currently snapped on X-axis, check if we should break out
                const distanceFromSnap = Math.abs(change.position.x - newSnapPosition.x);
                if (distanceFromSnap > breakoutThreshold) {
                  newSnapPosition.x = null; // Break out of snap
                } else {
                  finalPosition.x = newSnapPosition.x; // Stay snapped
                }
              } else if (bestSnapX !== null) {
                // Not currently snapped, but found a snap point
                finalPosition.x = bestSnapX;
                newSnapPosition.x = bestSnapX;
              }

              // Handle Y-axis snapping
              if (newSnapPosition.y !== null) {
                // Currently snapped on Y-axis, check if we should break out
                const distanceFromSnap = Math.abs(change.position.y - newSnapPosition.y);
                if (distanceFromSnap > breakoutThreshold) {
                  newSnapPosition.y = null; // Break out of snap
                } else {
                  finalPosition.y = newSnapPosition.y; // Stay snapped
                }
              } else if (bestSnapY !== null) {
                // Not currently snapped, but found a snap point
                finalPosition.y = bestSnapY;
                newSnapPosition.y = bestSnapY;
              }

              // Update snap position state only if it changed
              if (
                newSnapPosition.x !== currentSnapPosition.x ||
                newSnapPosition.y !== currentSnapPosition.y
              ) {
                setCurrentSnapPosition(newSnapPosition);
              }

              // Update the node position
              if (change.type === 'position') {
                (change as any).position = finalPosition;
              }
            }
          }
          }
        }
        
        if (change.type === 'position' && 'dragging' in change && !change.dragging) {
          // When drag ends, ensure the node stays at the snapped position if it was snapped
          if (currentSnapPosition && change.position) {
            const originalPosition = { ...change.position };
            const finalPosition = { ...change.position };
            
            // Apply final snapped positions
            if (currentSnapPosition.x !== null) {
              finalPosition.x = currentSnapPosition.x;
            }
            if (currentSnapPosition.y !== null) {
              finalPosition.y = currentSnapPosition.y;
            }
            
            // Debug logging
            logger.debug('Drag end - applying snapped position:', {
              nodeId: change.id,
              original: originalPosition,
              snapped: finalPosition,
              snapState: currentSnapPosition
            });
            
            // Update the change with the snapped position
            (change as any).position = finalPosition;
          }
          
          // Clear drag state after applying final position
          setDraggingNodeId(null);
          setHelperLines([]);
          setDragStartPosition(null);
          setCurrentSnapPosition({ x: null, y: null });
        }
      });

      // Apply the changes
      onNodesChange(changes);
    },
    [nodes, onNodesChange, dragStartPosition, currentSnapPosition, updateTabModified, pipelineId]
  );

  // Handle drag and drop
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      try {
        const type = event.dataTransfer.getData('application/reactflow');
        if (!type) return;

        logger.debug('Raw drag data:', type);
        const nodeInfo: NodeInfo = JSON.parse(type);
        logger.debug('Parsed nodeInfo:', nodeInfo);
        
        const position = {
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        };

        // Convert screen position to flow position
        const { x: flowX, y: flowY, zoom } = getViewport();
        const flowPosition: XYPosition = {
          x: (position.x - flowX) / zoom,
          y: (position.y - flowY) / zoom,
        };

        // Generate unique name to avoid conflicts
        const uniqueName = generateUniqueNodeName(nodeInfo.name);

        logger.flow.node.debug('Dropping node:', {
          nodeType: nodeInfo.nodeType,
          dataType: nodeInfo.type,
          originalName: nodeInfo.name,
          uniqueName,
          position: flowPosition,
          pipelineId,
        });

        // Create new node directly with store types
        const newNode: StoreNode = {
          id: getId(),
          type: nodeInfo.nodeType || 'unknown',
          position: flowPosition,
          data: {
            name: uniqueName,
            label: uniqueName,
            type: nodeInfo.type,
            iconType: nodeInfo.type,
            nodeType: nodeInfo.nodeType || 'unknown',
            svg: nodeInfo.icon || null,
            incomingFields: [],
            outgoingFields: [],
          },
        };

        logger.flow.node.debug('Created newNode:', newNode);
        addNode(newNode);

        // Mark tab as modified when new node is added
        updateTabModified(pipelineId, true);

        // Set the new node as selected
        setSelectedNodeId(newNode.id);
      } catch (error) {
        logger.error('Error parsing dropped data:', error);
      }
    },
    [pipelineId, addNode, setSelectedNodeId, getViewport, updateTabModified]
  );

  // Handle node clicks
  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Ignore clicks during drag
      if (draggingNodeId) {
        return;
      }

      // If node is already selected, preserve the existing double-click state
      // This prevents interfering with properties panel during async loading
      if (node.id === selectedNodeId) {
        const currentPipeline = useStore.getState().pipelines.find(p => p.id === pipelineId);
        const currentIsDoubleClicked = currentPipeline?.isNodeDoubleClicked || false;
        
        logger.flow.node.debug('🖱️ handleNodeClick (same node):', {
          nodeId: node.id,
          currentIsDoubleClicked,
          preservingState: currentIsDoubleClicked
        });
        
        // Preserve existing double-click state (don't change it on single click)
        setSelectedNodeId(node.id, currentIsDoubleClicked);
      } else {
        // Otherwise just select the node (new selection)
        logger.flow.node.debug('🖱️ handleNodeClick (new node):', { nodeId: node.id });
        setSelectedNodeId(node.id, false);
      }

      // CRITICAL FIX: Also update ReactFlow's selection state
      // This ensures onSelectionChange gets triggered and selectedNodes state is updated
      setSelectedNodes([node]);
    },
    [draggingNodeId, selectedNodeId, setSelectedNodeId, pipelineId, setSelectedNodes]
  );

  // Handle node double clicks
  const handleNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Ignore double clicks during drag
      if (draggingNodeId) {
        return;
      }
      
      // Check if this node type should show properties panel
      const showProperties = shouldShowPropertiesPanel(node.type || 'unknown');
      
      // Only set double-click state if the node type supports properties panel
      if (showProperties) {
        setSelectedNodeId(node.id, true);
      } else {
        // For nodes that don't support properties panel, just select them normally
        setSelectedNodeId(node.id, false);
      }
    },
    [draggingNodeId, setSelectedNodeId]
  );

  // Handle edge clicks
  const handleEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge<EdgeData>) => {
      // Check if Alt key is pressed for disable functionality
      if (event.altKey) {
        // Alt+click toggles the enabled state
        const newEnabledState = !(edge.data?.enabled ?? true);
        
        // Update the edge using updatePipeline to avoid race conditions with onEdgesChange
        const updatedEdges = edges.map(e => 
          e.id === edge.id 
            ? { 
                ...e, 
                data: { 
                  ...e.data, 
                  enabled: newEnabledState,
                  condition: e.data?.condition || 'unconditional',
                  fields: e.data?.fields ?? []
                } 
              }
            : e
        );
        updatePipeline(pipelineId, { edges: updatedEdges });
        
        // Mark tab as modified when edge enabled state is toggled
        updateTabModified(pipelineId, true);
        
        logger.debug('Edge enabled state toggled:', {
          edgeId: edge.id,
          enabled: newEnabledState
        });
        
        return;
      }
      
      // Normal click: cycle through edge conditions
      const currentCondition = edge.data?.condition || 'unconditional';
      const conditions: Array<'unconditional' | 'true' | 'false' | 'info' | 'error_handling'> = [
        'unconditional',
        'true', 
        'false',
        'info',
        'error_handling'
      ];
      
      const currentIndex = conditions.indexOf(currentCondition);
      const nextIndex = (currentIndex + 1) % conditions.length;
      const nextCondition = conditions[nextIndex];
      
      // Update the edge condition using updatePipeline to avoid race conditions
      const updatedEdges = edges.map(e => 
        e.id === edge.id 
          ? { 
              ...e, 
              data: { 
                ...e.data, 
                condition: nextCondition,
                enabled: e.data?.enabled ?? true,
                fields: e.data?.fields ?? []
              } 
            }
          : e
      );
      updatePipeline(pipelineId, { edges: updatedEdges });
      
      // Mark tab as modified when edge condition is changed
      updateTabModified(pipelineId, true);
      
      // Log the change for debugging
      logger.debug('Edge condition changed:', {
        edgeId: edge.id,
        from: currentCondition,
        to: nextCondition
      });
    },
    [edges, updatePipeline, pipelineId, updateTabModified]
  );

  // Handle context menu (right-click)
  const handlePaneContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      // Get clientX and clientY from either React.MouseEvent or MouseEvent
      const clientX = 'clientX' in event ? event.clientX : (event as any).clientX;
      const clientY = 'clientY' in event ? event.clientY : (event as any).clientY;

      // Convert screen position to flow position
      const { x: flowX, y: flowY, zoom } = getViewport();
      const flowPosition: XYPosition = {
        x: (clientX - reactFlowBounds.left - flowX) / zoom,
        y: (clientY - reactFlowBounds.top - flowY) / zoom,
      };

      setContextMenu({
        show: true,
        x: clientX,
        y: clientY,
        flowPosition,
      });
    },
    [getViewport]
  );

  // Handle context menu item clicks
  const handleAddNote = useCallback(() => {
    if (contextMenu.flowPosition) {
      // Create new note node
      const newNode: StoreNode = {
        id: getId(),
        type: 'note',
        position: contextMenu.flowPosition,
        data: {
          name: 'Note',
          label: 'Note',
          type: 'note',
          iconType: 'note',
          nodeType: 'note',
          svg: null,
          incomingFields: [],
          outgoingFields: [],
          content: '# Note',
          width: 200,
          height: 120,
          red: 254,
          green: 254,
          blue: 254,
          fontSize: 12,
        },
      };

      addNode(newNode);
      
      // Mark tab as modified when new note is added
      updateTabModified(pipelineId, true);
      
      // Save to history BEFORE adding the note
      saveToHistory('Add note');
      
      // Set the new note as selected
      setSelectedNodeId(newNode.id);
      
      logger.info('Note added:', newNode);
    }
    setContextMenu({ show: false, x: 0, y: 0 });
  }, [contextMenu.flowPosition, addNode, updateTabModified, pipelineId, setSelectedNodeId, saveToHistory]);

  // Close context menu on click outside
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0 });
  }, []);

  // Get current zoom level
  const zoom = getZoom();

  // Add keyboard event listener for delete key and undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const reactFlowElement = reactFlowWrapper.current;
      const isReactFlowFocused = reactFlowElement?.contains(document.activeElement);

      // Handle undo/redo shortcuts (Ctrl+Z, Ctrl+Y)
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          if (canUndo()) {
            undo();
          }
          return;
        }
        if ((event.key === 'y') || (event.key === 'z' && event.shiftKey)) {
          event.preventDefault();
          if (canRedo()) {
            redo();
          }
          return;
        }
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Only delete nodes if we're NOT in an input field or properties panel
        const activeElement = document.activeElement;
        const isInInputField = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.closest('.ant-input') || // Ant Design inputs
          activeElement.closest('.ant-select') || // Ant Design selects
          activeElement.closest('.ant-checkbox') || // Ant Design checkboxes
          activeElement.closest('.properties-panel') || // Properties panel
          activeElement.closest('[role="textbox"]') // Any textbox role
        );

        if (isInInputField) {
          // Let the input field handle the delete key normally
          return;
        }

        // Use our local selectedNodes state variable
        if (selectedNodes.length > 0) {
          event.preventDefault(); // Prevent any other handlers
          deleteSelectedWithLogging({
            selectedNodes: selectedNodes,
            selectedEdges: [], // TODO: Add edge selection support
            deleteNode,
            deleteEdge,
            updateTabModified,
            saveToHistory,
            markStructuralChange,
            pipelineId
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNodes, deleteNode, deleteEdge, updateTabModified, pipelineId, undo, redo, canUndo, canRedo]);

  // Add click listener to close context menu
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.show) {
        handleCloseContextMenu();
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [contextMenu.show, handleCloseContextMenu]);

  // Add copy and paste functionality
  const handleCopy = useCallback(
    (event: ClipboardEvent<Document>) => {
      // Only handle copy if ReactFlow canvas is focused
      const reactFlowElement = reactFlowWrapper.current;
      const isReactFlowFocused = reactFlowElement?.contains(document.activeElement);

      if (!isReactFlowFocused) {
        return; // Let other components handle the copy event
      }

      // Use our local selectedNodes state variable
      if (selectedNodes.length === 0) {
        return; // Nothing to copy
      }

      const pipelineData: PipelineData = {
        actionTransforms: selectedNodes.map((node) => ({
          name: (node.data?.label as string) || (node.data?.name as string) || '',
          type: node.type || '',
        })),
        hops: [], // For now, only copy nodes, not edges
      };

      const dataString = JSON.stringify(pipelineData, null, 2);
      event.clipboardData?.setData('text/plain', dataString);

      logger.flow.debug('Copied pipeline data:', {
        nodes: selectedNodes.length,
        edges: 0,
      });

      // event.preventDefault(); // Only prevent default when we actually handle the event
    },
    [selectedNodes]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<Document>) => {
      // Only handle paste if ReactFlow canvas is focused
      const reactFlowElement = reactFlowWrapper.current;
      const isReactFlowFocused = reactFlowElement?.contains(document.activeElement);

      if (!isReactFlowFocused) {
        return; // Let other components handle the paste event
      }

      try {
        const pastedData = event.clipboardData?.getData('text/plain');
        if (!pastedData) return;

        const pipelineData: PipelineData = JSON.parse(pastedData);
        if (!pipelineData.actionTransforms) return;

        // Create new nodes from pasted data with unique names
        const newNodes: StoreNode[] = pipelineData.actionTransforms.map((transform, index) => {
          const uniqueName = generateUniqueNodeName(transform.name);
          return {
            id: getId(),
            type: transform.type || 'genericStep',
            position: {
              x: 100 + index * 200,
              y: 100,
            },
            data: {
              name: uniqueName,
              label: uniqueName,
              type: transform.type,
              iconType: transform.type,
              nodeType: transform.type || 'genericStep',
              svg: null,
              incomingFields: [],
              outgoingFields: [],
              parallel: transform.parallel,
            },
          };
        });

        // Create new edges (simplified for now)
        const newEdges: StoreEdge[] = [];

        // Save to history BEFORE paste operation
        saveToHistory(`Paste ${newNodes.length} nodes, ${newEdges.length} edges`);
        
        // Update the nodes and edges using the existing state management
        onNodesChange(newNodes.map((node) => ({ type: 'add', item: node })));
        onEdgesChange(newEdges.map((edge) => ({ type: 'add', item: node })));

        // Log the operation
        logger.flow.debug('Pasted pipeline data:', {
          nodes: newNodes.length,
          edges: newEdges.length,
        });

        // Prevent default paste behavior only when we handle it
        // event.preventDefault();
      } catch (error) {
        logger.error('Failed to paste pipeline data:', error);
      }
    },
    [nodes, edges, onNodesChange, onEdgesChange, saveToHistory]
  );

  // Add keyboard event handlers for copy and paste
  useEffect(() => {
    // Add event listeners to the document
    document.addEventListener('copy', handleCopy as any);
    document.addEventListener('paste', handlePaste as any);

    // Cleanup
    return () => {
      document.removeEventListener('copy', handleCopy as any);
      document.removeEventListener('paste', handlePaste as any);
    };
  }, [handleCopy, handlePaste]);

  const [runDialogOpen, setRunDialogOpen] = useState(false);

  // Use extracted save hook
  const {
    handleSave,
    handleSaveFile,
    handleSaveAs,
    saveAsDialogOpen,
    setSaveAsDialogOpen,
  } = useCanvasSave({
    pipelineId,
    nodes,
    edges,
    tabType,
    hasStructuralChanges,
    getTransformState,
    updateTabName,
    migrateTabId,
    updateTabIsNew,
    migratePipelineId,
    clearStructuralChanges,
  });

  // Note: handleSaveAs is now provided by useCanvasSave hook

  return (
    <div style={reactFlowWrapperStyle} ref={reactFlowWrapper}>
      {/* Toolbar - fixed height */}
      <div
        style={{
          padding: '8px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: token.colorBgContainer,
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '8px',
          flexShrink: 0, // Prevent toolbar from shrinking
        }}
      >
        <Tooltip title={isTabModified ? `Save ${tabType === 'workflow' ? 'Workflow' : 'Pipeline'}` : "No changes to save"} placement="bottom">
          <Button 
            type="text" 
            icon={<SaveOutlined />} 
            onClick={handleSaveFile}
            disabled={!isTabModified}
            data-testid="save-button"
          />
        </Tooltip>
        <Tooltip title="Save As..." placement="bottom">
          <Button 
            type="text" 
            icon={<FolderOpenOutlined />} 
            onClick={() => setSaveAsDialogOpen(true)}
            data-testid="save-as-button"
          />
        </Tooltip>
        <div style={{ width: '1px', backgroundColor: '#f0f0f0', margin: '0 8px' }} />
        <Tooltip title="Undo (Ctrl+Z)" placement="bottom">
          <Button 
            type="text" 
            icon={<UndoOutlined />} 
            onClick={() => {
              telemetryService.trackUserInteraction('click', 'undo-button', { pipelineId });
              undo();
            }}
            disabled={!canUndo()}
            data-testid="undo-button"
          />
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Y)" placement="bottom">
          <Button 
            type="text" 
            icon={<RedoOutlined />} 
            onClick={() => {
              telemetryService.trackUserInteraction('click', 'redo-button', { pipelineId });
              redo();
            }}
            disabled={!canRedo()}
            data-testid="redo-button"
          />
        </Tooltip>
        <div style={{ width: '1px', backgroundColor: '#f0f0f0', margin: '0 8px' }} />
        <Tooltip title="Run Pipeline" placement="bottom">
          <Button 
            type="text" 
            icon={<PlayCircleOutlined />} 
            onClick={() => setRunDialogOpen(true)}
            data-testid="run-button"
          />
        </Tooltip>
        <Tooltip title="Preview Data" placement="bottom">
          <Button type="text" icon={<EyeOutlined />} style={{ visibility: 'hidden' }} />
        </Tooltip>
        <Tooltip title="Stop Execution" placement="bottom">
          <Button type="text" icon={<StopOutlined />} style={{ visibility: 'hidden' }} />
        </Tooltip>
        <div style={{ width: '1px', backgroundColor: '#f0f0f0', margin: '0 8px' }} />
        <Tooltip title="Horizontal Layout" placement="bottom">
          <Button
            type="text"
            icon={<BorderVerticleOutlined />}
            onClick={() => {
              const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                nodes,
                edges,
                'LR'
              );
              const nodeChanges: NodeChange[] = layoutedNodes.map((node) => ({
                id: node.id,
                type: 'position',
                position: node.position,
              }));
              onNodesChange(nodeChanges);
              // saveToHistory is now handled by onNodesChange for position changes
            }}
          />
        </Tooltip>
        <Tooltip title="Vertical Layout" placement="bottom">
          <Button
            type="text"
            icon={<BorderHorizontalOutlined />}
            onClick={() => {
              const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                nodes,
                edges,
                'TB'
              );
              const nodeChanges: NodeChange[] = layoutedNodes.map((node) => ({
                id: node.id,
                type: 'position',
                position: node.position,
              }));
              onNodesChange(nodeChanges);
              // saveToHistory is now handled by onNodesChange for position changes
            }}
          />
        </Tooltip>
      </div>

      {/* ReactFlow - takes remaining space */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onEdgeClick={handleEdgeClick}
          onSelectionChange={onSelectionChange}
          onPaneClick={() => {
            setSelectedNodes([]);
            setSelectedNodeId(null, false);
          }}
          onPaneContextMenu={handlePaneContextMenu}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          colorMode={isDarkMode ? 'dark' : 'light'}
          style={{ width: '100%', height: '100%' }}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          fitView={false}
          minZoom={0.1}
          maxZoom={4}
          deleteKeyCode={null}
          noWheelClassName="nowheel"
          nodesDraggable={true}
          nodeDragThreshold={1}
          defaultEdgeOptions={{
            type: 'custom',
            data: {
              condition: 'unconditional',
              enabled: true,
              fields: [],
            },
          }}
          proOptions={{ hideAttribution: true }}
          connectionLineStyle={{ stroke: '#ddd', strokeWidth: 2 }}
          connectionLineType={"bezier" as any}
        >
          <Controls />
          <MiniMap
            nodeComponent={CustomMiniMapNode}
            nodeStrokeColor={token.colorBorder}
            nodeColor={token.colorBorder}
            nodeStrokeWidth={2}
            nodeBorderRadius={4}
            pannable={true}
            zoomable={true}
            ariaLabel="Pipeline minimap overview"
          />
          <Background variant={"dots" as any} gap={12} size={1} />

          {/* Helper lines rendered inside ReactFlow - with viewport transformation */}
          <HelperLines lines={helperLines} viewport={getViewport()} />

          <Panel
            position="bottom-left"
            style={{
              padding: '5px 8px',
              fontSize: '12px',
              border: `1px solid ${token.colorBorder}`,
              borderRadius: '2px',
              backgroundColor: token.colorBgContainer,
              color: token.colorText,
              pointerEvents: 'none',
              userSelect: 'none',
              marginBottom: '130px', // Position above the controls panel
            }}
          >
            {Math.round(zoom * 100)}%
          </Panel>
        </ReactFlow>

        {/* Context Menu */}
        {contextMenu.show && (
          <div
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              backgroundColor: token.colorBgContainer,
              border: `1px solid ${token.colorBorder}`,
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1050,
              minWidth: '120px',
              padding: '4px 0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              data-testid="context-menu-add-note"
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s',
              }}
              onClick={handleAddNote}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              📝 Add Note
            </div>
          </div>
        )}
      </div>
      <RunPipelineDialog 
        open={runDialogOpen} 
        onClose={() => setRunDialogOpen(false)} 
        pipelineFile={pipelineId}
      />
      <FileBrowserDialog
        open={saveAsDialogOpen}
        onCancel={() => setSaveAsDialogOpen(false)}
        onSave={handleSaveAs}
        title={tabType === 'workflow' ? 'Save Workflow As' : 'Save Pipeline As'}
        fileExtension={tabType === 'workflow' ? '.hwf' : '.hpl'}
        mode="save"
      />
    </div>
  );
});

FlowCanvas.displayName = 'FlowCanvas';

export default HopCanvasViewer;
